import {
    CHAT_MESSAGE_TYPE,
    COPILOT_MESSAGE_TYPE,
    CopilotMessageStateOptions,
    type CopilotDisplayMessage,
} from "@regal-voice/shared-types";

const IN_PROGRESS_STATES: ReadonlySet<string> = new Set([
    CopilotMessageStateOptions.THINKING,
    CopilotMessageStateOptions.STREAMING,
]);

function isInProgress(state: string | undefined): boolean {
    return state != null && IN_PROGRESS_STATES.has(state);
}

/**
 * Forces all messages to ``FINISHED`` state. Called when a stream is confirmed
 * complete so that stale ``STREAMING`` / ``THINKING`` states (caused by React 18
 * batching races between the last ``updateMessages`` and the ``onComplete``
 * callback reading the ref) never persist into committed UI state.
 */
export function finalizeStreamMessages(messages: CopilotDisplayMessage[]): CopilotDisplayMessage[] {
    let changed = false;

    const result = messages.map((message): CopilotDisplayMessage => {
        if (message.messageType === CHAT_MESSAGE_TYPE.FUNCTION_CALL) {
            if (!isInProgress(message.responseState)) {
                return message;
            }
            changed = true;
            return { ...message, responseState: CopilotMessageStateOptions.FINISHED };
        }

        // Agent summaries are always finalized and carry no content blocks.
        if (message.messageType === COPILOT_MESSAGE_TYPE.AGENT_SUMMARY) {
            return message;
        }

        // Agent builder triggers are always finalized and carry no content blocks.
        if (message.messageType === COPILOT_MESSAGE_TYPE.AB_TRIGGER) {
            return message;
        }

        const needsResponseFix = isInProgress(message.responseState);
        const needsBlockFix = (message.contentBlocks ?? []).some((block) => isInProgress(block.state));

        if (!needsResponseFix && !needsBlockFix) {
            return message;
        }

        changed = true;

        return {
            ...message,
            responseState: needsResponseFix ? CopilotMessageStateOptions.FINISHED : message.responseState,
            contentBlocks: needsBlockFix
                ? (message.contentBlocks ?? []).map((block) =>
                      isInProgress(block.state) ? { ...block, state: CopilotMessageStateOptions.FINISHED } : block
                  )
                : message.contentBlocks,
        };
    });

    return changed ? result : messages;
}
